/**
 * The membership timeline's drawing, from events already loaded — split from
 * `MembershipTimeline.tsx` so it reaches no API client (and through it no
 * `import.meta.env`), and `verify-rule-builder` can render it from fixtures.
 * See that file for why people are masked here and what the totals are over.
 */
import { SEGMENT_MEMBERSHIP_CAUSE_LABEL, type SegmentMembershipEvent } from '@wend/shared';
import { EventLog, type EventLogItem } from '@wend/ui';
import { LogIn, LogOut } from 'lucide-react';

const EN_IN = new Intl.NumberFormat('en-IN');

const WHEN = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** "17 Oct, 11:40" in IST — the zone every rule is evaluated in. */
export function timelineWhen(iso: string): string {
  return WHEN.format(new Date(iso));
}

export function membershipItems(events: readonly SegmentMembershipEvent[]): EventLogItem[] {
  return events.map((event) => {
    const who = event.person.name ?? 'Someone without a name';
    return {
      key: event.id,
      when: timelineWhen(event.at),
      tone: event.kind === 'entered' ? 'good' : 'bad',
      icon: event.kind === 'entered' ? <LogIn aria-hidden="true" /> : <LogOut aria-hidden="true" />,
      title: `${who} ${event.kind === 'entered' ? 'entered' : 'left'}`,
      detail: [event.person.address ?? 'no address on file', SEGMENT_MEMBERSHIP_CAUSE_LABEL[event.cause]].join(' · '),
      aside: (
        <span className={event.kind === 'entered' ? 'text-good' : 'text-bad'} data-kind={event.kind}>
          {event.kind === 'entered' ? '+1' : '−1'}
        </span>
      ),
    };
  });
}

/** The log, from events already loaded — the part a static-markup suite renders. */
export function MembershipTimelineView({ events }: { events: readonly SegmentMembershipEvent[] }) {
  if (events.length === 0) {
    return (
      <p className="text-[0.88rem] text-muted" data-role="timeline-empty">
        Nobody has entered or left yet. The first refresh after an upload, a lead or a reply will show up here.
      </p>
    );
  }
  const entered = events.filter((event) => event.kind === 'entered').length;
  const left = events.length - entered;
  return (
    <EventLog
      label={`Latest ${EN_IN.format(events.length)} changes`}
      whenWidth="date"
      maxHeight={360}
      items={membershipItems(events)}
      totals={
        <>
          <span className="text-good">+{EN_IN.format(entered)} entered</span>{' '}
          <span className="text-bad">−{EN_IN.format(left)} left</span>
        </>
      }
    />
  );
}
